import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  listLocations,
  moveBox,
  quarantineBox,
  voidBox,
  type InventoryBox,
} from "@/lib/inventory/api";
import { useInventoryText } from "@/lib/inventory/i18n";
import {
  ConfirmReview,
  ErrorNotice,
  Field,
  Loading,
  conditionText,
  selectClass,
  useInventoryAccess,
  useInventoryWrite,
  useRead,
} from "./shared";

type Action = "move" | "quarantine" | "voidLabel";
export function InventoryBoxActions({
  box,
  onSaved,
  onBusy,
}: {
  box: InventoryBox;
  onSaved: () => Promise<void>;
  onBusy: (busy: boolean) => void;
}) {
  const t = useInventoryText();
  const access = useInventoryAccess();
  const [action, setAction] = useState<Action | null>(null);
  const [reason, setReason] = useState("");
  const [location, setLocation] = useState<number | "">("");
  const [condition, setCondition] = useState("QUARANTINED");
  const [review, setReview] = useState(false);
  const [saved, setSaved] = useState(false);
  const [refreshError, setRefreshError] = useState<unknown>(null);
  const write = useInventoryWrite();
  const locations = useRead(listLocations, []);
  useEffect(() => {
    onBusy(write.busy || review);
    return () => onBusy(false);
  }, [write.busy, review, onBusy]);
  const received = box.status !== "UNASSIGNED" && box.status !== "VOIDED";
  const valid =
    reason.trim().length > 0 &&
    reason.length <= 400 &&
    Number.isSafeInteger(box.row_version) &&
    (action !== "move" ||
      (!!location &&
        location !== box.location_id &&
        !!locations.data &&
        !locations.error &&
        !locations.busy));
  async function confirm() {
    if (!valid || !action) return;
    if (
      action === "quarantine"
        ? !(access.write || access.supervisor) ||
          (condition === "AVAILABLE" && !access.supervisor)
        : !access.write
    )
      return;
    const result = await write.run(
      {
        action,
        box_id: box.box_id,
        version: box.row_version,
        reason,
        location,
        condition,
      },
      async (key) => {
        if (action === "move")
          await moveBox(
            box.box_id,
            Number(location),
            reason,
            box.row_version,
            key,
          );
        else if (action === "quarantine")
          await quarantineBox(
            box.box_id,
            condition,
            reason,
            box.row_version,
            key,
          );
        else await voidBox(box.box_id, reason, box.row_version, key);
        await onSaved();
        return true;
      },
    );
    if (result) {
      setSaved(true);
      setAction(null);
      setReview(false);
      setReason("");
    }
  }
  if ((!access.write && !access.supervisor) || box.status === "VOIDED")
    return null;
  return (
    <div className="space-y-3">
      {saved && <p role="status">{t.saved}</p>}
      <div className="flex flex-wrap gap-3">
        {received && access.write && (
          <Button
            variant="secondary"
            disabled={write.busy || review}
            onClick={() => {
              setAction("move");
              setSaved(false);
            }}
          >
            {t.move}
          </Button>
        )}
        {received && (access.write || access.supervisor) && (
          <Button
            variant="secondary"
            disabled={write.busy || review}
            onClick={() => {
              setAction("quarantine");
              setSaved(false);
            }}
          >
            {t.quarantine}
          </Button>
        )}
        {box.status === "UNASSIGNED" && access.write && (
          <Button
            variant="secondary"
            disabled={write.busy || review}
            onClick={() => {
              setAction("voidLabel");
              setSaved(false);
            }}
          >
            {t.voidLabel}
          </Button>
        )}
      </div>
      {action && (
        <section
          aria-label={t[action]}
          className="space-y-3 rounded border border-border p-4"
        >
          <h3 className="font-510">{t[action]}</h3>
          {action === "move" && (
            <>
              {locations.busy && <Loading />}
              <ErrorNotice error={locations.error} />
              <Field label={t.location}>
                <select
                  className={selectClass}
                  disabled={
                    write.busy || review || locations.busy || !!locations.error
                  }
                  value={location}
                  onChange={(e) =>
                    setLocation(e.target.value ? Number(e.target.value) : "")
                  }
                >
                  <option value="">{t.select}</option>
                  {locations.data?.locations.map((l) => (
                    <option key={l.location_id} value={l.location_id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </Field>
            </>
          )}
          {action === "quarantine" && (
            <Field label={t.condition}>
              <select
                className={selectClass}
                disabled={write.busy || review}
                value={condition}
                onChange={(e) => setCondition(e.target.value)}
              >
                <option value="QUARANTINED">{t.quarantined}</option>
                {access.supervisor && (
                  <option value="AVAILABLE">{t.available}</option>
                )}
              </select>
            </Field>
          )}
          <Field label={t.reason}>
            <Input
              value={reason}
              maxLength={400}
              disabled={write.busy || review}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          <p>
            {t.version}: {box.row_version}
          </p>
          <ErrorNotice error={refreshError || write.error} />
          {!review && (
            <div className="flex gap-3">
              <Button
                disabled={!valid || write.busy || write.blocked}
                onClick={() => setReview(true)}
              >
                {t.review}
              </Button>
              <Button
                variant="secondary"
                disabled={write.busy}
                onClick={() => setAction(null)}
              >
                {t.cancel}
              </Button>
            </div>
          )}
          {review && (
            <ConfirmReview
              title={t.mutationReview}
              busy={write.busy}
              blocked={
                !valid || write.blocked || !(access.write || access.supervisor)
              }
              onConfirm={() => void confirm()}
              onCancel={() => setReview(false)}
            >
              <p>
                {t[action]} · {box.box_id} · {t.version}: {box.row_version}
              </p>
              {action === "move" && (
                <p>
                  {t.location}:{" "}
                  {
                    locations.data?.locations.find(
                      (l) => l.location_id === location,
                    )?.name
                  }
                </p>
              )}
              {action === "quarantine" && (
                <p>
                  {t.condition}: {conditionText(t, condition)}
                </p>
              )}
              <p className="whitespace-pre-wrap">
                {t.reason}: {reason}
              </p>
            </ConfirmReview>
          )}
          {write.blocked && (
            <Button
              variant="secondary"
              onClick={() => {
                setReview(false);
                locations.refresh();
                void onSaved()
                  .then(() => {
                    write.clear();
                    setRefreshError(null);
                  })
                  .catch(setRefreshError);
              }}
            >
              {t.retry}
            </Button>
          )}
        </section>
      )}
    </div>
  );
}
